import type {
  CreateListingRequest,
  UpdateListingRequest,
  WindowedResponse,
} from '@pulsify/shared/dto'
import { createListingRequestSchema, updateListingRequestSchema } from '@pulsify/shared/dto'
import * as m from '~/paraglide/messages'
import { assertValidPayload } from '~/shared/lib/assert-valid-payload'
import { api, unwrap } from '~/shared/lib/rpc'
import type {
  CreateListingInput,
  OpeningHour,
  OpeningHours,
  UpdateListingInput,
} from '~/shared/types'
import type { listingBackendSchema, listingSchema } from '~/shared/types/listing'
import { splitNicknames } from '../lib/nicknames'

type Listing = ReturnType<typeof listingSchema.parse>
type ListingBackend = ReturnType<typeof listingBackendSchema.parse>

interface ListingListParams {
  startIndex: number
  pageSize: number
}

const DAY_TO_ENUM: Record<string, string> = {
  Sunday: '0',
  Monday: '1',
  Tuesday: '2',
  Wednesday: '3',
  Thursday: '4',
  Friday: '5',
  Saturday: '6',
}

function convertHoursToBackendFormat(hours: OpeningHour[]): OpeningHours {
  const result: Record<string, { open: string; close: string }[]> = {}

  hours.forEach((hour) => {
    const dayEnum = DAY_TO_ENUM[hour.day]
    if (!dayEnum) return

    const open = hour.open.substring(0, 5)
    const close = hour.close.substring(0, 5)
    if (!open || !close) return

    if (!result[dayEnum]) {
      result[dayEnum] = []
    }

    result[dayEnum].push({ open, close })
  })

  return result as OpeningHours
}

const ENUM_TO_DAY: Record<string, OpeningHour['day']> = {
  '0': 'Sunday',
  '1': 'Monday',
  '2': 'Tuesday',
  '3': 'Wednesday',
  '4': 'Thursday',
  '5': 'Friday',
  '6': 'Saturday',
}

function normalizeDay(key: string): OpeningHour['day'] | undefined {
  if (key in DAY_TO_ENUM) return key as OpeningHour['day']
  return ENUM_TO_DAY[key]
}

// The backend returns hours as an array of { day, open, close } (one entry per
// time range). A legacy dict shape ({ "1": [{ open, close }] }) is still handled
// for safety.
function convertHoursToUIFormat(
  hours: OpeningHour[] | Record<string, { open: string; close: string }[]>,
): OpeningHour[] {
  if (Array.isArray(hours)) {
    const result: OpeningHour[] = []
    for (const entry of hours) {
      const day = normalizeDay(entry.day)
      if (!day) continue
      result.push({ day, open: entry.open, close: entry.close })
    }
    return result
  }

  const result: OpeningHour[] = []
  for (const [key, ranges] of Object.entries(hours)) {
    const day = normalizeDay(key)
    if (!day) continue
    for (const range of ranges) {
      result.push({ day, open: range.open, close: range.close })
    }
  }
  return result
}

const EN_LOCALE = { locale: 'en' } as const

const LISTING_FIELD_MESSAGES: Record<string, () => string> = {
  'location.street': () => m.streetRequired(undefined, EN_LOCALE),
  'location.city': () => m.cityRequired(undefined, EN_LOCALE),
  'location.country': () => m.countryRequired(undefined, EN_LOCALE),
  'tags.mainCategoryId': () => m.categoryRequired(undefined, EN_LOCALE),
  'tags.subCategoryTags': () => m.pleaseSelectAtLeastOneSubcategory(undefined, EN_LOCALE),
}

export const listingsApi = {
  async list(businessId: string, params: ListingListParams): Promise<WindowedResponse<Listing>> {
    const res = await api.api.businesses[':businessId'].listings.$get({
      param: { businessId },
      query: {
        startIndex: String(params.startIndex),
        pageSize: String(params.pageSize),
      },
    })
    const body = await unwrap<WindowedResponse<ListingBackend>>(res)
    return {
      data: body.data.map((listing) => ({
        ...listing,
        hours: listing.hours ? convertHoursToUIFormat(listing.hours) : [],
      })),
      window: body.window,
    }
  },

  async getById(businessId: string, listingId: string): Promise<Listing> {
    const res = await api.api.businesses[':businessId'].listings[':id'].$get({
      param: { businessId, id: listingId },
    })
    const listing = await unwrap<ListingBackend>(res)
    return {
      ...listing,
      hours: listing.hours ? convertHoursToUIFormat(listing.hours) : [],
    }
  },

  async create(businessId: string, data: CreateListingInput): Promise<{ id: string }> {
    const payload: CreateListingRequest = {
      name: data.name,
      description: data.description,
      location: {
        name: data.location.name || null,
        street: data.location.street,
        city: data.location.city,
        country: data.location.country,
      },
      postalCode: data.postalCode?.trim() || null,
      priceLevel: data.priceLevel ? Number(data.priceLevel) : null,
      phone: data.phone || null,
      website: data.website || null,
      email: data.email || null,
      facebook: data.facebook || null,
      instagram: data.instagram || null,
      twitter: data.twitter || null,
      nicknames: splitNicknames(data.nicknames),
      hours: convertHoursToBackendFormat(data.hours || []),
      tags: {
        mainCategoryId: data.tags.mainCategoryId,
        subCategoryTags: data.tags.subCategoryTags,
        subSubCategoryTags: data.tags.subSubCategoryTags || [],
        amenityIds: data.tags.amenityIds || [],
      },
    }

    assertValidPayload(createListingRequestSchema, payload, LISTING_FIELD_MESSAGES)

    const res = await api.api.businesses[':businessId'].listings.$post({
      param: { businessId },
      json: payload,
    })
    return unwrap<{ id: string }>(res)
  },

  async update(
    businessId: string,
    listingId: string,
    data: Partial<UpdateListingInput>,
  ): Promise<void> {
    const payload: UpdateListingRequest = {
      name: data.name,
      description: data.description,
      location: {
        name: data.location?.name || null,
        street: data.location?.street || '',
        city: data.location?.city || '',
        country: data.location?.country || 'Austria',
      },
      phone: data.phone || null,
      website: data.website || null,
      email: data.email || null,
      facebook: data.facebook || null,
      instagram: data.instagram || null,
      twitter: data.twitter || null,
      ...(data.nicknames === undefined ? {} : { nicknames: splitNicknames(data.nicknames) }),
      ...(data.postalCode === undefined ? {} : { postalCode: data.postalCode.trim() || null }),
      ...(data.priceLevel === undefined
        ? {}
        : { priceLevel: data.priceLevel ? Number(data.priceLevel) : null }),
      hours: convertHoursToBackendFormat(data.hours || []),
      tags: data.tags
        ? {
            mainCategoryId: data.tags.mainCategoryId,
            subCategoryTags: data.tags.subCategoryTags,
            subSubCategoryTags: data.tags.subSubCategoryTags || [],
            amenityIds: data.tags.amenityIds || [],
          }
        : undefined,
    }

    assertValidPayload(updateListingRequestSchema, payload, LISTING_FIELD_MESSAGES)

    const res = await api.api.businesses[':businessId'].listings[':id'].$put({
      param: { businessId, id: listingId },
      json: payload,
    })
    await unwrap<void>(res)
  },

  async publish(businessId: string, listingId: string): Promise<void> {
    const res = await api.api.businesses[':businessId'].listings[':id'].status.$post({
      param: { businessId, id: listingId },
      json: { action: 'publish' },
    })
    await unwrap<void>(res)
  },

  async delete(businessId: string, listingId: string): Promise<void> {
    const res = await api.api.businesses[':businessId'].listings[':id'].$delete({
      param: { businessId, id: listingId },
    })
    await unwrap<void>(res)
  },
}
